import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  MagnifyingGlass,
  User,
  MapPin,
  Tag,
  Star,
  BookmarkSimple,
  Note,
  Funnel,
  X,
  Plus,
  Eye,
  Lightning,
  Users,
  CaretDown,
  Trash,
  PencilSimple
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUserDatabase = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [savedPairs, setSavedPairs] = useState([]);
  
  // Search & Filter state
  const [filters, setFilters] = useState({
    q: '',
    gender: '',
    location: '',
    age_min: '',
    age_max: '',
    relationship_goal: '',
    interests: '',
    tags: '',
    priority: '',
    matching_status: '',
    faith: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection for pairing
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showPairDialog, setShowPairDialog] = useState(false);
  const [pairNotes, setPairNotes] = useState('');
  
  // Tag dialog
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [tagUser, setTagUser] = useState(null);
  const [newTag, setNewTag] = useState('');
  
  // Note dialog
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteUser, setNoteUser] = useState(null);
  const [newNote, setNewNote] = useState('');

  const fetchUsers = useCallback(async (filtersOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const activeFilters = filtersOverride || filters;
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API}/admin/users/search?${params}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/tags`);
      setAllTags(response.data);
    } catch (error) {
      // Tag fetch failed silently — non-critical
    }
  }, []);

  const fetchSavedPairs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/saved-pairs`);
      setSavedPairs(response.data);
    } catch (error) {
      // Saved pairs fetch failed silently — non-critical
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchTags();
    fetchSavedPairs();
  }, [fetchUsers, fetchTags, fetchSavedPairs]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const clearFilters = () => {
    setFilters({
      q: '',
      gender: '',
      location: '',
      age_min: '',
      age_max: '',
      relationship_goal: '',
      interests: '',
      tags: '',
      priority: '',
      matching_status: '',
      faith: ''
    });
  };

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else if (selectedUsers.length < 2) {
      const newSelection = [...selectedUsers, userId];
      setSelectedUsers(newSelection);
      if (newSelection.length === 2) {
        setShowPairDialog(true);
      }
    }
  };

  const savePotentialPair = async () => {
    if (selectedUsers.length !== 2) return;
    
    try {
      await axios.post(`${API}/admin/saved-pair`, {
        user_a_id: selectedUsers[0],
        user_b_id: selectedUsers[1],
        notes: pairNotes
      });
      toast.success('Potential pair saved!');
      setShowPairDialog(false);
      setSelectedUsers([]);
      setPairNotes('');
      fetchSavedPairs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pair');
    }
  };

  const addTag = async () => {
    if (!tagUser || !newTag.trim()) return;
    
    try {
      await axios.post(`${API}/admin/user/${tagUser.id}/tag`, { 
        user_id: tagUser.id,
        tag: newTag.trim() 
      });
      toast.success('Tag added');
      setShowTagDialog(false);
      setNewTag('');
      fetchUsers();
      fetchTags();
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const removeTag = async (userId, tag) => {
    try {
      await axios.delete(`${API}/admin/user/${userId}/tag/${tag}`);
      toast.success('Tag removed');
      fetchUsers();
      fetchTags();
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  const addNote = async () => {
    if (!noteUser || !newNote.trim()) return;
    
    try {
      await axios.post(`${API}/admin/user/${noteUser.id}/note`, {
        user_id: noteUser.id,
        note: newNote.trim()
      });
      toast.success('Note added');
      setShowNoteDialog(false);
      setNewNote('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const updatePriority = async (userId, priority) => {
    try {
      await axios.put(`${API}/admin/user/${userId}/priority`, { priority });
      toast.success('Priority updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const updateStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/user/${userId}/status`, { status });
      toast.success('Status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteUserQuick = async (u) => {
    const confirmed = window.confirm(
      `Permanently delete ${u.name} (${u.email}) and ALL their data?\n\nThis cannot be undone. For a softer option, use Ban or Archive instead.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/user/${u.id}`);
      toast.success(`${u.name} deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-700';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-700',
      paused: 'bg-amber-100 text-amber-700',
      matched: 'bg-pink-100 text-pink-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-blue-100 text-blue-700';
  };

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart weight="fill" className="w-6 h-6 text-primary" />
              <span className="font-serif text-xl text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 hover:text-white">
                <SignOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <House className="w-4 h-4 inline mr-2" />Overview
            </Link>
            <Link to="/admin/applications" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <ClipboardText className="w-4 h-4 inline mr-2" />Applications
            </Link>
            <Link to="/admin/database" className="py-4 text-white border-b-2 border-primary text-sm font-medium">
              <Users className="w-4 h-4 inline mr-2" />User Database
            </Link>
            <Link to="/admin/matching" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <Handshake className="w-4 h-4 inline mr-2" />Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2" data-testid="database-title">
              User Database
            </h1>
            <p className="text-stone-400">
              {users.length} users • Search, organize, and find matches
            </p>
          </div>
          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-pink-400">{selectedUsers.length} selected</span>
              <Button variant="ghost" size="sm" onClick={() => setSelectedUsers([])} className="text-stone-400">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="bg-stone-800 rounded-lg p-6 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input
                  placeholder="Search by name, email, location, interests..."
                  value={filters.q}
                  onChange={(e) => setFilters(prev => ({ ...prev, q: e.target.value }))}
                  className="pl-10 bg-stone-700 border-stone-600 text-white"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="btn-primary">
                <MagnifyingGlass className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-stone-600 text-stone-300">
                <Funnel className="w-4 h-4 mr-2" />
                Filters
                <CaretDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-stone-700">
                <Select value={filters.gender} onValueChange={(v) => setFilters(prev => ({ ...prev, gender: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue placeholder="Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Genders</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Location"
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  className="bg-stone-700 border-stone-600 text-white"
                />

                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Age min"
                    value={filters.age_min}
                    onChange={(e) => setFilters(prev => ({ ...prev, age_min: e.target.value }))}
                    className="bg-stone-700 border-stone-600 text-white w-20"
                  />
                  <Input
                    type="number"
                    placeholder="Age max"
                    value={filters.age_max}
                    onChange={(e) => setFilters(prev => ({ ...prev, age_max: e.target.value }))}
                    className="bg-stone-700 border-stone-600 text-white w-20"
                  />
                </div>

                <Select value={filters.relationship_goal} onValueChange={(v) => setFilters(prev => ({ ...prev, relationship_goal: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue placeholder="Goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Goal</SelectItem>
                    <SelectItem value="marriage">Marriage</SelectItem>
                    <SelectItem value="long_term">Long-Term</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="friendship">Friendship</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.faith} onValueChange={(v) => setFilters(prev => ({ ...prev, faith: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue placeholder="Faith" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Faith</SelectItem>
                    <SelectItem value="christian">Christian</SelectItem>
                    <SelectItem value="catholic">Catholic</SelectItem>
                    <SelectItem value="spiritual">Spiritual</SelectItem>
                    <SelectItem value="none">No affiliation</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.priority} onValueChange={(v) => setFilters(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Priority</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.matching_status} onValueChange={(v) => setFilters(prev => ({ ...prev, matching_status: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Interests (comma sep)"
                  value={filters.interests}
                  onChange={(e) => setFilters(prev => ({ ...prev, interests: e.target.value }))}
                  className="bg-stone-700 border-stone-600 text-white"
                />

                <Button type="button" variant="ghost" onClick={clearFilters} className="text-stone-400">
                  Clear Filters
                </Button>
              </div>
            )}
          </form>

          {/* Quick Tag Filters */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-stone-700">
              <span className="text-stone-500 text-sm mr-2">Quick Tags:</span>
              {allTags.slice(0, 10).map((t) => (
                <button
                  key={t.tag}
                  onClick={() => setFilters(prev => ({ ...prev, tags: t.tag }))}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    filters.tags === t.tag 
                      ? 'bg-primary text-white border-primary' 
                      : 'border-stone-600 text-stone-400 hover:border-primary hover:text-primary'
                  }`}
                >
                  {t.tag} ({t.count})
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* User List */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {loading ? (
                <div className="text-stone-400 text-center py-12">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-stone-400 text-center py-12">No users found matching your criteria</div>
              ) : (
                users.map((u) => (
                  <div
                    key={u.id}
                    className={`bg-stone-800 rounded-lg p-5 transition-all ${
                      selectedUsers.includes(u.id) ? 'ring-2 ring-primary' : ''
                    }`}
                    data-testid={`user-card-${u.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Selection Checkbox */}
                      <button
                        onClick={() => toggleUserSelection(u.id)}
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors ${
                          selectedUsers.includes(u.id)
                            ? 'bg-primary border-primary text-white'
                            : 'border-stone-600 hover:border-primary'
                        }`}
                      >
                        {selectedUsers.includes(u.id) && <Heart weight="fill" className="w-4 h-4" />}
                      </button>

                      {/* Avatar */}
                      <div className="w-14 h-14 rounded-lg bg-stone-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {u.application?.photo_base64 ? (
                          <img src={u.application.photo_base64} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User weight="light" className="w-7 h-7 text-stone-500" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-serif text-lg text-white">{u.name}</h3>
                          <span className="text-stone-400 text-sm">{u.age} • {u.gender}</span>
                          {/* Application status pill — shows for everyone, including non-applied */}
                          {(() => {
                            if (!u.application) {
                              return (
                                <span className="text-xs px-2 py-0.5 rounded bg-stone-600 text-stone-200">
                                  Not Started
                                </span>
                              );
                            }
                            if (u.application.is_draft) {
                              return (
                                <span className="text-xs px-2 py-0.5 rounded bg-amber-600 text-white" data-testid={`app-status-draft-${u.id}`}>
                                  In Progress
                                </span>
                              );
                            }
                            return (
                              <span className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white" data-testid={`app-status-submitted-${u.id}`}>
                                Submitted
                              </span>
                            );
                          })()}
                          {u.priority && (
                            <span className={`text-xs px-2 py-0.5 rounded border ${getPriorityColor(u.priority)}`}>
                              {u.priority}
                            </span>
                          )}
                          {u.matching_status && (
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(u.matching_status)}`}>
                              {u.matching_status}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-sm text-stone-400 mb-2">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {u.location}
                          </span>
                          {u.application?.relationship_goal && (
                            <span className="text-pink-400">{u.application.relationship_goal}</span>
                          )}
                          {u.notes_count > 0 && (
                            <span className="flex items-center gap-1 text-amber-400">
                              <Note className="w-4 h-4" />
                              {u.notes_count} notes
                            </span>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(u.tags || []).map((tag) => (
                            <span
                              key={`${u.id}-tag-${tag}`}
                              className="text-xs px-2 py-0.5 rounded-full bg-stone-700 text-stone-300 flex items-center gap-1"
                            >
                              <Tag className="w-3 h-3" />
                              {tag}
                              <button onClick={() => removeTag(u.id, tag)} className="hover:text-red-400">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => { setTagUser(u); setShowTagDialog(true); }}
                            className="text-xs px-2 py-0.5 rounded-full border border-dashed border-stone-600 text-stone-500 hover:border-primary hover:text-primary"
                          >
                            <Plus className="w-3 h-3 inline" /> Add Tag
                          </button>
                        </div>

                        {/* Interests Preview */}
                        {u.application?.interests && (
                          <div className="flex flex-wrap gap-1">
                            {u.application.interests.slice(0, 5).map((interest) => (
                              <span key={`${u.id}-int-${interest}`} className="text-xs px-2 py-0.5 rounded bg-pink-900/30 text-pink-300">
                                {interest}
                              </span>
                            ))}
                            {u.application.interests.length > 5 && (
                              <span className="text-xs text-stone-500">+{u.application.interests.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Link to={`/admin/user/${u.id}`}>
                          <Button size="sm" variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-700 w-full" data-testid={`view-user-${u.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </Link>
                        <Link to={`/admin/user/${u.id}`}>
                          <Button size="sm" variant="ghost" className="text-stone-400 hover:text-blue-400 w-full" data-testid={`edit-user-${u.id}`}>
                            <PencilSimple className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setNoteUser(u); setShowNoteDialog(true); }}
                          className="text-stone-400 hover:text-amber-400"
                        >
                          <Note className="w-4 h-4 mr-1" /> Note
                        </Button>
                        <Link to={`/admin/matching?user=${u.id}`}>
                          <Button size="sm" className="btn-primary w-full">
                            <Lightning className="w-4 h-4 mr-1" /> Match
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteUserQuick(u)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>

                    {/* Quick Status/Priority Controls */}
                    <div className="flex gap-4 mt-4 pt-4 border-t border-stone-700">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">Priority:</span>
                        {['high', 'medium', 'low'].map((p) => (
                          <button
                            key={p}
                            onClick={() => updatePriority(u.id, p)}
                            className={`text-xs px-2 py-1 rounded capitalize ${
                              u.priority === p ? getPriorityColor(p) : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-500">Status:</span>
                        {['active', 'paused', 'matched'].map((s) => (
                          <button
                            key={s}
                            onClick={() => updateStatus(u.id, s)}
                            className={`text-xs px-2 py-1 rounded capitalize ${
                              u.matching_status === s ? getStatusColor(s) : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Saved Pairs Sidebar */}
          <div>
            <div className="bg-stone-800 rounded-lg p-6 sticky top-24">
              <h2 className="font-serif text-xl text-white mb-4 flex items-center gap-2">
                <BookmarkSimple weight="fill" className="w-5 h-5 text-primary" />
                Saved Pairs
              </h2>
              <p className="text-stone-400 text-sm mb-4">
                Select 2 users to save as a potential match
              </p>

              {savedPairs.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-8">No saved pairs yet</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {savedPairs.map((pair) => (
                    <div key={pair.id} className="bg-stone-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white text-sm">{pair.user_a?.name}</span>
                        <Heart weight="fill" className="w-4 h-4 text-primary" />
                        <span className="text-white text-sm">{pair.user_b?.name}</span>
                      </div>
                      {pair.notes && (
                        <p className="text-stone-400 text-xs mb-2">{pair.notes}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          pair.status === 'matched' ? 'bg-pink-900/50 text-pink-300' :
                          pair.status === 'contacted' ? 'bg-blue-900/50 text-blue-300' :
                          'bg-stone-600 text-stone-300'
                        }`}>
                          {pair.status}
                        </span>
                        <Link to={`/admin/matching?user=${pair.user_a_id}`}>
                          <Button size="sm" variant="ghost" className="text-xs text-primary hover:text-primary">
                            Create Match
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Save Pair Dialog */}
      <Dialog open={showPairDialog} onOpenChange={setShowPairDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Save Potential Pair</DialogTitle>
            <DialogDescription className="text-stone-400">
              Save these two users as a potential match for later review
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center justify-center gap-4 mb-4">
              {selectedUsers.map((id) => {
                const u = users.find(user => user.id === id);
                return u ? (
                  <div key={id} className="text-center">
                    <div className="w-16 h-16 rounded-full bg-stone-700 mx-auto mb-2 flex items-center justify-center">
                      <User className="w-8 h-8 text-stone-400" />
                    </div>
                    <p className="text-white text-sm">{u.name}</p>
                  </div>
                ) : null;
              })}
              {selectedUsers.length === 2 && (
                <Heart weight="fill" className="w-8 h-8 text-primary mx-2" />
              )}
            </div>
            <Textarea
              placeholder="Notes about why they might be a good match..."
              value={pairNotes}
              onChange={(e) => setPairNotes(e.target.value)}
              className="bg-stone-700 border-stone-600 text-white"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowPairDialog(false); setSelectedUsers([]); }} className="text-stone-400">
              Cancel
            </Button>
            <Button onClick={savePotentialPair} className="btn-primary">
              <BookmarkSimple className="w-4 h-4 mr-2" />
              Save Pair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Tag to {tagUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter tag (e.g., professional, outdoorsy, faith-focused)"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              className="bg-stone-700 border-stone-600 text-white"
            />
            {allTags.length > 0 && (
              <div className="mt-4">
                <p className="text-stone-400 text-sm mb-2">Existing tags:</p>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((t) => (
                    <button
                      key={t.tag}
                      onClick={() => setNewTag(t.tag)}
                      className="text-xs px-2 py-1 rounded bg-stone-700 text-stone-300 hover:bg-stone-600"
                    >
                      {t.tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTagDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={addTag} className="btn-primary">
              <Tag className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Note for {noteUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Write a private note about this user..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="bg-stone-700 border-stone-600 text-white"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNoteDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={addNote} className="btn-primary">
              <Note className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserDatabase;
